import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShieldCheck, Star, TrendingUp, ArrowRight, Package, Zap, Truck, Award, Eye, Flame, Heart, BookOpen, Clock, ChevronRight, Recycle, RefreshCw, Target, Sparkles, Trophy } from 'lucide-react';
import { listingsAPI, photoAPI, blogAPI, bannersAPI } from '../lib/api';
import { useFavorites } from '../context/FavoritesContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import NewsTicker from '../components/NewsTicker';
import { useTranslation } from 'react-i18next';

const HERO_IMG = 'https://images.unsplash.com/photo-1709587823868-735f9375ae74?w=1400&q=80';

// Blog images by category
const BLOG_CATEGORY_IMAGES = {
  guide: 'https://images.unsplash.com/photo-1658491830143-72808ca237e3?w=800&q=80',
  comparatif: 'https://images.unsplash.com/photo-1738330194751-e096b51af7bf?w=800&q=80',
  test: 'https://images.unsplash.com/photo-1657704022321-105c8d9723ed?w=800&q=80',
  actu: 'https://images.unsplash.com/photo-1657704358775-ed705c7388d2?w=800&q=80',
};

const getBlogImage = (article) => {
  if (article.image && article.image.startsWith('http')) return article.image;
  return BLOG_CATEGORY_IMAGES[article.category] || BLOG_CATEGORY_IMAGES.guide;
};

const condKey = (s) => s >= 9 ? 'home.cond_new' : s >= 7 ? 'home.cond_very_good' : s >= 5 ? 'home.cond_good' : 'home.cond_fair';
const condColor = (s) => s >= 10 ? 'bg-emerald-500' : s >= 9 ? 'bg-green-500' : s >= 7 ? 'bg-sky-500' : s >= 5 ? 'bg-amber-500' : 'bg-orange-500';

const ProductCard = ({ listing, size = 'normal' }) => {
  const { t } = useTranslation();
  const photoUrl = listing.photos?.[0] ? photoAPI.getUrl(listing.photos[0]) : null;
  const modelName = listing.model.replace(new RegExp('^' + listing.brand + '\\s+', 'i'), '');
  const discount = listing.market_price ? Math.round((1 - listing.price / listing.market_price) * 100) : 0;
  const isFeatured = size === 'large';
  const { isFavorite, toggleFavorite } = useFavorites();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const fav = isFavorite(listing.id);

  const handleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) { navigate('/login'); return; }
    toggleFavorite(listing.id);
  };

  return (
    <Link to={`/annonce/${listing.id}`} className="group" data-testid={`product-${listing.id}`}>
      <div className={`bg-white rounded-2xl border border-[#E8F4FD] overflow-hidden hover:shadow-xl hover:border-transparent transition-all duration-300 hover:-translate-y-1 ${isFeatured ? 'ring-1 ring-[#32CD32]/20' : ''}`}>
        <div className={`${isFeatured ? 'aspect-square' : 'aspect-[4/3]'} bg-[#F0F7FA] relative overflow-hidden`}>
          {photoUrl ? (
            <img src={photoUrl} alt={modelName} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#B8D4E3]">
              <Package className="w-12 h-12" />
            </div>
          )}
          <span className={`absolute top-2.5 left-2.5 px-2 py-0.5 rounded-lg text-[10px] font-bold text-white ${condColor(listing.condition_score)} shadow-sm`}>
            {t(condKey(listing.condition_score))}
          </span>
          {discount > 0 && (
            <span className="absolute bottom-2.5 left-2.5 px-2 py-1 rounded-lg text-[10px] font-bold bg-red-500 text-white shadow-sm">
              -{discount}%
            </span>
          )}
          <button onClick={handleFav}
            className={`absolute top-2.5 right-2.5 w-8 h-8 rounded-full flex items-center justify-center shadow-md transition-all duration-200 ${fav ? 'bg-red-50 scale-110' : 'bg-white/90 backdrop-blur-sm hover:bg-red-50 opacity-0 group-hover:opacity-100'}`}
            data-testid={`fav-toggle-${listing.id}`}>
            <Heart className={`w-4 h-4 transition-colors ${fav ? 'text-red-500 fill-red-500' : 'text-[#5A8BA8]'}`} />
          </button>
        </div>
        <div className={`${isFeatured ? 'p-5' : 'p-3.5'}`}>
          <p className="text-[10px] text-[#5A8BA8] uppercase tracking-wider font-semibold">{listing.brand}</p>
          <h3 className={`font-bold text-[#033871] mt-0.5 line-clamp-1 group-hover:text-[#0C63BF] transition-colors ${isFeatured ? 'text-base' : 'text-sm'}`}>{modelName}</h3>
          <div className="flex items-baseline gap-2 mt-2">
            <span className={`font-black text-[#0C63BF] ${isFeatured ? 'text-2xl' : 'text-lg'}`}>{listing.price.toFixed(0)}&euro;</span>
            {listing.market_price && (
              <span className="text-xs text-[#B8D4E3] line-through">{listing.market_price.toFixed(0)}&euro;</span>
            )}
          </div>
          {isFeatured && (
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[#F0F7FA]">
              <div className="flex items-center gap-1 text-xs text-[#5A8BA8]">
                {listing.seller_rating ? (
                  <><Star className="w-3 h-3 fill-amber-400 text-amber-400" /><span className="font-medium">{listing.seller_rating.toFixed(1)}</span></>
                ) : (
                  <span className="text-[10px]">{t('home.verified_profile')}</span>
                )}
              </div>
              <div className="flex items-center gap-1 text-[10px] text-[#B8D4E3]">
                <Eye className="w-3 h-3" /><span>{listing.views}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

const HomePage = () => {
  const { t } = useTranslation();
  const [recentListings, setRecentListings] = useState([]);
  const [popularListings, setPopularListings] = useState([]);
  const [blogArticles, setBlogArticles] = useState([]);
  const [promoBanners, setPromoBanners] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    listingsAPI.getAll({ limit: 12, sort_by: 'created_at', sort_order: 'desc' })
      .then(res => setRecentListings(res.data.listings))
      .catch(() => {});
    listingsAPI.getAll({ limit: 4, sort_by: 'views', sort_order: 'desc' })
      .then(res => setPopularListings(res.data.listings))
      .catch(() => {});
    blogAPI.getArticles()
      .then(res => setBlogArticles(res.data.slice(0, 3)))
      .catch(() => {});
    bannersAPI.getActive()
      .then(res => setPromoBanners(res.data.banners?.filter(b => b.position === 'promo') || []))
      .catch(() => {});
  }, []);

  const featured = recentListings[0];
  const rest = recentListings.slice(1);

  return (
    <div className="min-h-screen bg-[#F7FAFB]">
      <SEO
        title={t('seo.default.title').replace(/^PadSmash \| /, '')}
        description={t('seo.default.description')}
        path="/"
      />
      {/* Compact Hero */}
      <section className="relative overflow-hidden bg-[#021E47]" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#021E47]/80 via-[#021E47]/55 to-[#021E47]/30" />
        </div>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative z-10 py-8 sm:py-12 lg:py-16">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#32CD32]/15 border border-[#32CD32]/30 rounded-full px-3 py-1 mb-3 sm:mb-4">
              <Zap className="w-3 h-3 text-[#32CD32]" />
              <span className="text-[#32CD32] text-[10px] font-semibold tracking-wide uppercase">{t('home.hero_badge')}</span>
            </div>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white leading-[1.1] tracking-tight" data-testid="hero-title">
              {t('home.hero_title_1')}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#32CD32] to-[#5fd88a]">{t('home.hero_title_2')}</span>
            </h1>
            <p className="text-sm lg:text-base text-[#7AA8C2] mt-3 max-w-lg leading-relaxed">
              {t('home.hero_sub')}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-5 sm:mt-6">
              <Link to="/catalogue">
                <Button size="lg" className="bg-[#32CD32] hover:bg-[#28a428] text-white h-10 sm:h-11 px-5 sm:px-6 text-sm font-bold gap-2 rounded-xl shadow-lg shadow-[#32CD32]/25 w-full sm:w-auto" data-testid="browse-btn">
                  <Search className="w-4 h-4" /> {t('home.browse_btn')}
                </Button>
              </Link>
              <Link to="/vendre">
                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 h-10 sm:h-11 px-5 sm:px-6 text-sm font-bold gap-2 rounded-xl w-full sm:w-auto" data-testid="sell-btn">
                  {t('home.sell_btn')}
                </Button>
              </Link>
              <Link to="/estimer">
                <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white h-10 sm:h-11 px-5 sm:px-6 text-sm font-bold gap-2 rounded-xl shadow-lg shadow-orange-500/25 w-full sm:w-auto" data-testid="estimate-hero-btn">
                  <TrendingUp className="w-4 h-4" /> {t('home.estimate_btn')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <div className="bg-white border-b border-[#E8F4FD]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-center gap-3 sm:gap-6 lg:gap-10 flex-wrap">
            {[
              { icon: ShieldCheck, text: t('home.trust_payment') },
              { icon: Truck, text: t('home.trust_delivery') },
              { icon: TrendingUp, text: t('home.trust_argus') },
              { icon: Award, text: t('home.trust_condition') },
              { icon: Star, text: t('home.trust_sellers') },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[#5A8BA8]">
                <item.icon className="w-4 h-4 text-[#32CD32]" />
                <span className="text-[10px] sm:text-xs font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* News Ticker - Padel Pro headlines */}
      <NewsTicker />

      {/* FEATURED + RECENT LISTINGS - Main product section */}
      {recentListings.length > 0 && (
        <section className="py-6 sm:py-10 bg-[#F7FAFB]" data-testid="products-section">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            {/* Section header */}
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <span className="text-xs font-bold text-orange-500 uppercase tracking-widest">{t('home.recent_badge')}</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-black text-[#033871]" data-testid="recent-heading">{t('home.recent_heading')}</h2>
              </div>
              <Link to="/catalogue" className="hidden sm:flex items-center gap-1 text-[#0C63BF] font-semibold text-sm hover:gap-2 transition-all" data-testid="see-all-link">
                {t('home.see_all')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Featured layout: 1 large + grid */}
            {featured && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4 mb-3 sm:mb-4">
                {/* Featured large card */}
                <div className="lg:col-span-4" data-testid="featured-listing">
                  <ProductCard listing={featured} size="large" />
                </div>
                {/* Grid of recent */}
                <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  {rest.slice(0, 6).map(l => (
                    <ProductCard key={l.id} listing={l} />
                  ))}
                </div>
              </div>
            )}

            {/* More listings */}
            {rest.length > 6 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mt-3 sm:mt-4">
                {rest.slice(6).map(l => (
                  <ProductCard key={l.id} listing={l} />
                ))}
              </div>
            )}

            {/* See all CTA */}
            <div className="text-center mt-8">
              <Link to="/catalogue">
                <Button size="lg" variant="outline" className="border-[#0C63BF] text-[#0C63BF] hover:bg-[#0C63BF] hover:text-white rounded-xl font-bold gap-2 h-11 px-8" data-testid="see-all-btn">
                  {t('home.see_all_btn')} <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* PadCoach + PadPro — twin feature banners (above brands strip) */}
      <section className="py-6 sm:py-10 bg-gradient-to-br from-[#F0F7FA] via-white to-[#F0F7FA]" data-testid="features-banner-section">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="text-center mb-5 sm:mb-7">
            <span className="inline-flex items-center gap-1.5 bg-[#32CD32]/15 border border-[#32CD32]/30 rounded-full px-3 py-1 text-[10px] font-semibold text-[#28a428] uppercase tracking-wide">
              <Sparkles className="w-3 h-3" /> {t('home.features_badge')}
            </span>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-black text-[#033871] mt-3">{t('home.features_heading')}</h2>
            <p className="text-sm text-[#5A8BA8] mt-1">{t('home.features_sub')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {/* PadCoach banner */}
            <Link to="/guide" data-testid="banner-padcoach"
              className="group relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#033871] via-[#0C63BF] to-[#033871] p-5 sm:p-7 text-white shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all">
              <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#32CD32]/20 blur-3xl group-hover:scale-110 transition-transform" />
              <div className="absolute -bottom-10 -left-8 w-32 h-32 rounded-full bg-[#32CD32]/10 blur-2xl" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-9 h-9 rounded-xl bg-[#32CD32]/20 border border-[#32CD32]/30 flex items-center justify-center">
                    <Target className="w-4 h-4 text-[#32CD32]" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-[#32CD32] font-bold">PadCoach</span>
                </div>
                <h3 className="text-lg sm:text-2xl font-black leading-tight mb-2" dangerouslySetInnerHTML={{ __html: t('home.pc_title') }} />
                <p className="text-white/75 text-xs sm:text-sm mb-4 max-w-sm">
                  {t('home.pc_desc')}
                </p>
                <span className="inline-flex items-center gap-1.5 bg-[#32CD32] text-white text-xs sm:text-sm font-bold px-4 py-2 rounded-xl shadow-lg group-hover:bg-[#28a428] group-hover:gap-2 transition-all">
                  {t('home.pc_cta')} <ArrowRight className="w-4 h-4" />
                </span>
                <div className="mt-4 flex items-center gap-3 text-[10px] sm:text-xs text-white/60">
                  <span>{t('home.pc_meta_1')}</span>
                  <span>·</span>
                  <span>{t('home.pc_meta_2')}</span>
                  <span>·</span>
                  <span>{t('home.pc_meta_3')}</span>
                </div>
              </div>
            </Link>

            {/* PadPro banner */}
            <Link to="/padpro" data-testid="banner-padpro"
              className="group relative rounded-2xl overflow-hidden bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 p-5 sm:p-7 text-white shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all">
              <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/15 blur-3xl group-hover:scale-110 transition-transform" />
              <div className="absolute -bottom-10 -left-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-9 h-9 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center">
                    <Trophy className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-white font-bold">PadPro</span>
                </div>
                <h3 className="text-lg sm:text-2xl font-black leading-tight mb-2" dangerouslySetInnerHTML={{ __html: t('home.pp_title') }} />
                <p className="text-white/85 text-xs sm:text-sm mb-4 max-w-sm">
                  {t('home.pp_desc')}
                </p>
                <span className="inline-flex items-center gap-1.5 bg-white text-amber-600 text-xs sm:text-sm font-bold px-4 py-2 rounded-xl shadow-lg group-hover:gap-2 transition-all">
                  {t('home.pp_cta')} <ArrowRight className="w-4 h-4" />
                </span>
                <div className="mt-4 flex items-center gap-3 text-[10px] sm:text-xs text-white/70">
                  <span>{t('home.pp_meta_1')}</span>
                  <span>·</span>
                  <span>{t('home.pp_meta_2')}</span>
                  <span>·</span>
                  <span>{t('home.pp_meta_3')}</span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>      {/* Popular / Most viewed */}
      {popularListings.length > 0 && (
        <section className="py-6 sm:py-10 bg-white" data-testid="popular-section">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="w-4 h-4 text-[#0C63BF]" />
                  <span className="text-xs font-bold text-[#0C63BF] uppercase tracking-widest">{t('home.popular_badge')}</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-black text-[#033871]" data-testid="popular-heading">{t('home.popular_heading')}</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              {popularListings.map(l => (
                <ProductCard key={l.id} listing={l} size="large" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Price Estimator CTA */}
      <section className="py-8 sm:py-12 bg-gradient-to-br from-[#0C63BF] to-[#033871]" data-testid="estimator-section">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 mb-3">
                <TrendingUp className="w-3.5 h-3.5 text-[#32CD32]" />
                <span className="text-[#32CD32] text-[10px] font-semibold uppercase tracking-wide">{t('home.est_badge')}</span>
              </div>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-black text-white leading-tight">
                {t('home.est_heading')}
              </h2>
              <p className="text-white/70 mt-2 text-sm sm:text-base max-w-md">
                {t('home.est_sub')}
              </p>
              <Link to="/estimer" className="mt-5 inline-block">
                <Button size="lg" className="bg-[#32CD32] hover:bg-[#28a428] text-white h-11 px-7 font-bold gap-2 rounded-xl shadow-lg shadow-black/20 text-sm" data-testid="estimate-cta-btn">
                  {t('home.estimate_btn')} <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="w-full md:w-auto flex justify-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/10 text-center">
                <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-[#32CD32] to-[#28a428] flex items-center justify-center mb-3">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
                <p className="text-white/60 text-xs mb-1">{t('home.est_ai')}</p>
                <p className="text-3xl font-black text-white">106€</p>
                <p className="text-white/40 text-[10px] mt-1">{t('home.est_example')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promo Banners */}
      {promoBanners.length > 0 && (
        <section className="py-6 sm:py-10 bg-[#F7FAFB]" data-testid="promo-banners-section">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {promoBanners.map(banner => (
                <Link key={banner.id} to={banner.link || '#'} className="group" data-testid={`promo-banner-${banner.id}`}>
                  <div className="relative rounded-2xl overflow-hidden h-40 sm:h-48">
                    <img src={banner.image_url} alt={banner.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-white font-bold text-base sm:text-lg">{banner.title}</h3>
                      {banner.subtitle && <p className="text-white/80 text-xs sm:text-sm mt-1">{banner.subtitle}</p>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Section - SEO Boost */}
      {blogArticles.length > 0 && (
        <section className="py-8 sm:py-12 bg-white" data-testid="blog-section">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <BookOpen className="w-4 h-4 text-[#0C63BF]" />
                  <span className="text-xs font-bold text-[#0C63BF] uppercase tracking-widest">Blog</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-black text-[#033871]">{t('home.blog_heading')}</h2>
                <p className="text-sm text-[#5A8BA8] mt-1">{t('home.blog_sub')}</p>
              </div>
              <Link to="/blog" className="hidden sm:flex items-center gap-1 text-[#0C63BF] font-semibold text-sm hover:underline">
                {t('home.blog_all')} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {blogArticles.map(article => (
                <Link key={article.id} to={`/blog/${article.slug}`} className="group" data-testid={`blog-${article.slug}`}>
                  <article className="bg-[#F7FAFB] rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                    <div className="h-36 relative overflow-hidden">
                      <img 
                        src={getBlogImage(article)} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                      <span className={`absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        article.category === 'guide' ? 'bg-blue-100 text-blue-700' :
                        article.category === 'comparatif' ? 'bg-purple-100 text-purple-700' :
                        article.category === 'test' ? 'bg-amber-100 text-amber-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {article.category === 'guide' ? t('home.cat_guide') : article.category === 'comparatif' ? t('home.cat_comparatif') : article.category === 'test' ? t('home.cat_test') : t('home.cat_actu')}
                      </span>
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-[#B8D4E3] text-xs mb-2">
                        <Clock className="w-3 h-3" /> {article.read_time} min
                      </div>
                      <h3 className="text-sm font-bold text-[#033871] group-hover:text-[#0C63BF] transition-colors line-clamp-2 flex-1">
                        {article.title}
                      </h3>
                      <span className="inline-flex items-center text-[#0C63BF] font-semibold text-xs gap-1 mt-3 group-hover:gap-2 transition-all">
                        {t('home.blog_read')} <ChevronRight className="w-3 h-3" />
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
            <div className="text-center mt-6 sm:hidden">
              <Link to="/blog">
                <Button variant="outline" className="border-[#0C63BF] text-[#0C63BF] font-bold gap-2">
                  {t('home.blog_see_all')} <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Impact Stats Block */}
      <section className="py-8 sm:py-12 bg-gradient-to-br from-[#F0F7FA] to-white" data-testid="impact-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl border border-[#E8F4FD] shadow-lg overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Left side - Stats */}
              <div className="p-6 sm:p-8 bg-gradient-to-br from-[#033871] to-[#0C63BF]">
                <div className="flex items-center gap-2 mb-4">
                  <Recycle className="w-5 h-5 text-[#32CD32]" />
                  <span className="text-[#32CD32] text-xs font-bold uppercase tracking-wide">{t('home.impact_badge')}</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-white leading-tight mb-4" dangerouslySetInnerHTML={{ __html: t('home.impact_heading') }} />
                <p className="text-white/70 text-sm leading-relaxed mb-6">
                  {t('home.impact_desc')}
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-2xl sm:text-3xl font-black text-[#32CD32]">70%</p>
                    <p className="text-white/60 text-[10px] sm:text-xs mt-1">{t('home.impact_stat_1')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl sm:text-3xl font-black text-[#32CD32]">-60%</p>
                    <p className="text-white/60 text-[10px] sm:text-xs mt-1">{t('home.impact_stat_2')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl sm:text-3xl font-black text-[#32CD32]">3-4x</p>
                    <p className="text-white/60 text-[10px] sm:text-xs mt-1">{t('home.impact_stat_3')}</p>
                  </div>
                </div>
              </div>
              {/* Right side - CTA */}
              <div className="p-6 sm:p-8 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#32CD32]/10 flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 text-[#32CD32]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#033871]">{t('home.mission_title')}</h4>
                    <p className="text-xs text-[#5A8BA8]">{t('home.mission_sub')}</p>
                  </div>
                </div>
                <p className="text-[#5A8BA8] text-sm leading-relaxed mb-6">
                  {t('home.mission_desc_1')}<strong className="text-[#033871]">{t('home.mission_strong')}</strong>{t('home.mission_desc_2')}
                </p>
                <Link to="/catalogue">
                  <Button className="w-full sm:w-auto bg-[#32CD32] hover:bg-[#28a428] text-white font-bold h-12 px-8 rounded-xl gap-2 shadow-lg shadow-[#32CD32]/20" data-testid="discover-rackets-btn">
                    {t('home.discover_btn')} <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>      {/* Quick brand access */}
      <section className="py-6 sm:py-10 bg-[#F7FAFB]" data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <h2 className="text-lg sm:text-2xl font-black text-[#033871] mb-4 sm:mb-6">{t('home.brands_heading')}</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 sm:gap-4">
            {[
              { name: 'Bullpadel', logo: '/brands/bullpadel.png' },
              { name: 'Head', logo: '/brands/head.png' },
              { name: 'Nox', logo: '/brands/nox.png' },
              { name: 'Babolat', logo: '/brands/babolat.png' },
              { name: 'Adidas', logo: '/brands/adidas.png' },
              { name: 'Wilson', logo: '/brands/wilson.png' },
            ].map(brand => (
              <Link key={brand.name} to={`/catalogue?brand=${brand.name.toLowerCase()}`}
                className="bg-white rounded-2xl border-2 border-[#E8F4FD] p-5 sm:p-6 flex items-center justify-center hover:shadow-xl hover:border-[#0C63BF] hover:shadow-[#0C63BF]/10 transition-all duration-300 hover:-translate-y-1 group aspect-square"
                data-testid={`brand-${brand.name.toLowerCase()}`}>
                <img src={brand.logo} alt={brand.name} className="h-14 sm:h-16 md:h-20 object-contain opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How it works - compact */}
      <section className="py-6 sm:py-10 bg-white" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-2xl font-black text-[#033871]">{t('home.how_heading')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5">
            {[
              { icon: Package, title: t('home.how_1_title'), desc: t('home.how_1_desc'), color: '#32CD32', step: '1' },
              { icon: ShieldCheck, title: t('home.how_2_title'), desc: t('home.how_2_desc'), color: '#0C63BF', step: '2' },
              { icon: Truck, title: t('home.how_3_title'), desc: t('home.how_3_desc'), color: '#033871', step: '3' },
            ].map(item => (
              <div key={item.step} className="flex items-start gap-4 p-5 bg-[#F7FAFB] rounded-2xl">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${item.color}12` }}>
                  <item.icon className="w-5 h-5" style={{ color: item.color }} />
                </div>
                <div>
                  <h3 className="font-bold text-[#033871] text-sm mb-1">{item.title}</h3>
                  <p className="text-[#5A8BA8] text-xs leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/comment-ca-marche">
              <Button variant="outline" className="rounded-xl font-semibold gap-2 text-[#0C63BF] border-[#0C63BF]/30 hover:bg-[#E8F4FD]" data-testid="how-it-works-more-btn">
                {t('home.how_more')} <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#033871] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-10 sm:py-14 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-[#32CD32]/15 border border-[#32CD32]/30 rounded-full px-3 py-1 mb-4">
            <Award className="w-3 h-3 text-[#32CD32]" />
            <span className="text-[#32CD32] text-[10px] font-semibold uppercase tracking-wide">{t('home.cta_badge')}</span>
          </div>
          <h2 className="text-lg sm:text-2xl lg:text-3xl font-black text-white leading-tight">
            {t('home.cta_title_1')}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#32CD32] to-[#5fd88a]">{t('home.cta_title_2')}</span>
          </h2>
          <p className="text-[#7AA8C2] mt-3 max-w-md mx-auto text-sm">
            {t('home.cta_sub')}
          </p>
          <div className="flex gap-3 justify-center mt-6">
            <Link to="/vendre">
              <Button size="lg" className="bg-[#32CD32] hover:bg-[#28a428] text-white h-11 px-7 font-bold gap-2 rounded-xl shadow-lg shadow-[#32CD32]/25 text-sm" data-testid="cta-sell-btn">
                {t('home.sell_btn')} <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
